import React, { useCallback, useEffect, useRef, useState } from 'react';
import { FaSearch, FaTimes } from 'react-icons/fa';
import { usePhaserKeyboardSuspend } from '@/hooks/usePhaserKeyboardSuspend';

/** Long enough that a typed word is one request, short enough to feel live. */
const DEBOUNCE_MS = 300;

export interface SearchInputProps {
  /** Called with the trimmed term, once the typing has settled. */
  onChange: (value: string) => void;
  placeholder?: string;
  /** What the field starts with; it owns the value from then on. */
  initialValue?: string;
  /** Override the debounce, in milliseconds. */
  delay?: number;
  ariaLabel?: string;
  className?: string;
}

/**
 * The console's search box (§8). Every admin list is server-side searched with
 * `q`, so the field debounces: without it, one request per keystroke would
 * arrive out of order and the operator would watch the table flicker through
 * the prefixes of what they were typing.
 *
 * The field keeps its own value and reports the settled one upward. Clearing is
 * reported immediately — the operator asked for the whole list back, and making
 * them wait a third of a second for it would only feel broken.
 */
export function SearchInput({
  onChange,
  placeholder = 'Search…',
  initialValue = '',
  delay = DEBOUNCE_MS,
  ariaLabel = 'Search',
  className = ''
}: SearchInputProps) {
  const [value, setValue] = useState(initialValue);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const phaserKeyboard = usePhaserKeyboardSuspend();

  // Read through a ref so a caller that passes a fresh closure on every render
  // does not restart the timer, and so the timer always calls the latest one.
  const onChangeRef = useRef(onChange);
  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  const cancel = useCallback(() => {
    if (timerRef.current !== null) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  // A pending term must not be reported after the field is gone: the tab that
  // would answer it has been unmounted too.
  useEffect(() => cancel, [cancel]);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const next = event.target.value;
    setValue(next);
    cancel();
    timerRef.current = setTimeout(() => {
      timerRef.current = null;
      // Trimmed: a trailing space is how a term looks halfway through being
      // typed, not a search anybody means, and the server trims `q` anyway.
      onChangeRef.current(next.trim());
    }, delay);
  };

  const handleClear = () => {
    cancel();
    setValue('');
    onChangeRef.current('');
  };

  return (
    <div className={`relative ${className}`.trim()}>
      <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 text-sm pointer-events-none" />
      <input
        type="search"
        value={value}
        onChange={handleChange}
        onFocus={phaserKeyboard.onFocus}
        onBlur={phaserKeyboard.onBlur}
        placeholder={placeholder}
        aria-label={ariaLabel}
        className="w-full pl-9 pr-9 py-2 bg-game-cream border border-game-medium/50 rounded-lg text-gray-900 placeholder-gray-600 focus:border-game-golden focus:outline-none focus:shadow-lg focus:shadow-game-golden/20 transition-all duration-200"
      />
      {value !== '' && (
        <button
          type="button"
          onClick={handleClear}
          aria-label="Clear search"
          className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 text-gray-500 hover:text-gray-800 rounded-md transition-colors duration-150"
        >
          <FaTimes className="text-sm" />
        </button>
      )}
    </div>
  );
}
